import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../lib/auth/useAuth';
import { useAccount } from '../../lib/account/useAccount';
import { ACCOUNT_ROLE_LABELS } from '../../lib/account/role-labels';
import { getInitials } from '../../lib/initials';
import { FeedbackDialog } from '../feedback/FeedbackDialog';
import { Popover } from '../ui/Popover';
import { AccountSwitcher } from './AccountSwitcher';

const menuItemClass =
  'flex w-full items-center rounded-md px-3 py-2 text-left text-sm text-secondary transition-colors hover:bg-bg-muted focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary';

/** Round initials button in the top bar: who is signed in, a switch between their accounts, plus feedback and sign out. */
export function AccountMenu() {
  const { user, logout } = useAuth();
  const { account, role } = useAccount();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-label="Account menu"
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary transition-colors hover:bg-primary/20 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      >
        {getInitials(user?.name, user?.email)}
      </button>

      {open && (
        <Popover
          triggerRef={triggerRef}
          onClose={() => setOpen(false)}
          ariaLabel="Account menu"
          widthPx={288}
          widthClass="w-72"
          bodyClassName="p-0"
        >
          <div className="border-b border-border px-4 py-3">
            {user?.name && <div className="truncate text-sm font-medium text-secondary">{user.name}</div>}
            <div className="truncate text-xs text-text-muted">{user?.email}</div>
            {account?.name && (
              <div className="mt-2 flex items-center justify-between gap-2 text-xs text-text-muted">
                <span className="truncate">{account.name}</span>
                {role && <span className="shrink-0">{ACCOUNT_ROLE_LABELS[role]}</span>}
              </div>
            )}
          </div>
          <AccountSwitcher onClose={() => setOpen(false)} />
          <div className="p-1.5">
            <Link to="/referral" className={`${menuItemClass} no-underline`} onClick={() => setOpen(false)}>
              Refer a colleague
            </Link>
            <button
              type="button"
              className={menuItemClass}
              onClick={() => {
                setOpen(false);
                setFeedbackOpen(true);
              }}
            >
              Send feedback
            </button>
            <button type="button" className={menuItemClass} onClick={logout}>
              Sign out
            </button>
          </div>
        </Popover>
      )}

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </>
  );
}
